import { useEffect, useState } from 'react'
import { BUILD_TIME, buildLabel, buildTimeLabel, COMMIT_TIME } from '../lib/buildInfo'

/**
 * Build metadata rendered as compact pill badges, stacked vertically:
 * - top pill: build number · commit time (UTC) · commit age — how old the
 *   deployed code is;
 * - bottom pill: image build time (UTC) · build age — how fresh the deployment
 *   is. Omitted when no build time is baked in (local dev).
 *
 * Used in the login footer and the sidebar user section. The ages are
 * recomputed on mount and every 60s so they never go stale in a long-lived
 * session; both surfaces share this component, so they always show the same
 * values. Colors and padding are supplied by the caller via `className`;
 * this component owns only the pill shape + typography.
 */
export function BuildBadge({ className = '' }: { className?: string }) {
  const [labels, setLabels] = useState(() => ({ commit: buildLabel(), built: buildTimeLabel() }))

  useEffect(() => {
    // No timestamps → labels never change, nothing to refresh.
    if (!COMMIT_TIME && !BUILD_TIME) return
    const id = setInterval(
      () => setLabels({ commit: buildLabel(), built: buildTimeLabel() }),
      60_000,
    )
    return () => clearInterval(id)
  }, [])

  const pillClassName = `inline-flex items-center rounded-md border font-mono text-[10px] leading-none tracking-tight whitespace-nowrap ${className}`

  return (
    <span className="inline-flex flex-col items-center gap-1">
      <span title="Build number · commit time (UTC) · commit age" className={pillClassName}>
        {labels.commit}
      </span>
      {labels.built && (
        <span title="Image build time (UTC) · build age" className={pillClassName}>
          {labels.built}
        </span>
      )}
    </span>
  )
}
